import { type ReactNode, useEffect, useState } from "react";
import { Icon, type IconName } from "./Icon.tsx";
import styles from "./StatePanel.module.css";
import ui from "./ui.module.css";

type Props = {
  icon: IconName;
  tone?: "danger" | "info";
  title: string;
  detail?: string;
  /** `h2` when the panel sits under a page title. */
  heading?: "h1" | "h2";
  children?: ReactNode;
};

export function StatePanel({
  icon,
  tone = "danger",
  title,
  detail,
  heading: Heading = "h1",
  children,
}: Props) {
  const message = detail ? `${title}. ${detail}` : title;
  const [announced, setAnnounced] = useState("");

  // The live region has to exist before its text for a screen reader to speak it.
  useEffect(() => {
    const timer = setTimeout(() => setAnnounced(message));
    return () => {
      clearTimeout(timer);
      setAnnounced("");
    };
  }, [message]);

  return (
    <div className={styles.frame}>
      <div className={`${ui.card} ${styles.panel}`} data-tone={tone}>
        <span className={styles.icon}>
          <Icon name={icon} />
        </span>
        <Heading className={styles.title}>{title}</Heading>
        {detail ? <p className={styles.detail}>{detail}</p> : null}
        {children ? <div className={styles.actions}>{children}</div> : null}
      </div>
      <p className={ui.visuallyHidden} role={tone === "danger" ? "alert" : "status"}>
        {announced}
      </p>
    </div>
  );
}
